// TypeFieldsFieldRow.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './TypeFieldsFieldRow.css';

// Icons
import Icon from 'svg/Icon';

const FIELD_TYPE_ICON_KEYS = {
  activity: 'timeline',
  barcode: 'barcode',
  boolean: 'toggle_on',
  checklist: 'checklist',
  comments: 'chat',
  cover: 'image',
  money: 'attach_money',
  currency: 'payments',
  date: 'calendar_today',
  daterange: 'date_range',
  datetime: 'schedule',
  datetimerange: 'event_repeat',
  subtitle: 'short_text',
  email: 'mail',
  enum: 'list_alt',
  location: 'location_on',
  number: 'tag',
  people: 'group',
  percentage: 'percent',
  phone: 'call',
  richtext: 'article',
  signature: 'draw',
  string: 'title',
  tags: 'sell',
  url: 'link',
};

/**
 * TypeFieldsFieldRow
 *
 * Renders one editable field row with field icon, label, settings action, and drag handle.
 *
 * @param {object} field - Field definition for the rendered row.
 * @param {boolean} isDragging - Whether this row is currently being dragged.
 * @param {boolean} isDropTarget - Whether this row is the current drag-over target.
 * @param {boolean} isSystemType - Whether type editing is disabled for system types.
 * @param {function} onDragEnd - Handler for drag end.
 * @param {function} onDragOver - Handler for drag over.
 * @param {function} onDragStart - Handler for drag start.
 * @param {function} onDrop - Handler for drop.
 * @param {function} onSettingsClick - Handler for opening field settings.
 *
 * @returns {ReactNode} The rendered field row.
 */
const TypeFieldsFieldRow = React.memo(({
  field,
  isDragging,
  isDropTarget,
  isSystemType,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSettingsClick,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const iconKey = useMemo(() => {
    const fieldType = String(field?.type || '').trim();
    return FIELD_TYPE_ICON_KEYS[fieldType] || 'category';
  }, [field?.type]);

  const fieldDescription = String(field?.fieldDescription || '').trim();

  // ----- RENDER ----- //

  return (
    <div
      className={`type-fields-field-row-container ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
      style={{
        backgroundColor: isDropTarget ? theme.backgroundHover : theme.panel,
        borderColor: isDropTarget ? theme.foregroundMuted : theme.backgroundHover,
        cursor: isSystemType ? 'default' : 'move',
      }}
      draggable={!isSystemType}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
    >

      {/* MAIN */}
      <div className="type-fields-field-row-main">

        {/* ICON */}
        <div className="type-fields-field-row-icon">
          <Icon
            iconKey={iconKey}
            width={18}
            height={18}
            color={theme.foregroundMuted}
          />
        </div>

        {/* COPY */}
        <div className="type-fields-field-row-copy">

          {/* LABEL */}
          <div
            className="type-fields-field-row-label"
          >
            {field?.label || field?.name}
          </div>

          {/* DESCRIPTION */}
          {fieldDescription ? (
            <div
              className="type-fields-field-row-description"
            >
              {fieldDescription}
            </div>
          ) : null}
        </div>
      </div>

      {/* ACTIONS */}
      <div className="type-fields-field-row-actions">

        {/* ACTION */}
        <button
          className="type-fields-field-row-action"
          disabled={isSystemType}
          title="Edit field"
          type="button"
          onClick={onSettingsClick}
        >
          <Icon
            iconKey="settings"
            width={16}
            height={16}
            color={theme.foregroundMuted}
          />
        </button>

        {/* DRAG HANDLE */}
        <div
          className="type-fields-field-row-drag-handle"
          title={isSystemType ? 'System types cannot be reordered.' : 'Drag to reorder'}
        >
          <Icon
            iconKey="drag_indicator"
            width={18}
            height={18}
            color={theme.foregroundMuted}
          />
        </div>
      </div>
    </div>
  );
});

// Dev Tools
TypeFieldsFieldRow.displayName = 'TypeFieldsFieldRow';

export default TypeFieldsFieldRow;
